/**
 * Administration route group.
 *
 * Compliance, KYB, branch administration, notifications, product factory,
 * limits, engine/provider admin, fees, rewards admin, open banking admin,
 * config store, rules and neural engine admin routes.
 *
 * Extracted verbatim from AppRoutes.tsx. The declaration sequence is preserved
 * exactly as part of the route structural contract; see
 * `__tests__/AppRoutes.structure.test.tsx`.
 */

import { lazy } from 'react';
import { Route, Navigate, useParams } from 'react-router-dom';
import { ProtectedRoute } from '../../auth/components/ProtectedRoute';
import { ROUTES, permissionsFor } from '../routeCatalogue';

// ---------------------------------------------------------------------------
// Lazy-loaded route pages
// ---------------------------------------------------------------------------

const ComplianceDashboard = lazy(() => import('../../features/compliance/pages/ComplianceDashboard'));
const AlertManagement = lazy(() => import('../../features/compliance/pages/AlertManagement'));
const SarFiling = lazy(() => import('../../features/compliance/pages/SarFiling'));
const RegulatoryReturns = lazy(() => import('../../features/compliance/pages/RegulatoryReturns'));
const SanctionsScreening = lazy(() => import('../../features/compliance/pages/SanctionsScreening'));
const AdminSARManagement = lazy(() => import('../../pages/compliance/SARManagement'));
const AdminSanctionsScreening = lazy(() => import('../../pages/compliance/SanctionsScreening'));
const AdminTransactionAlerts = lazy(() => import('../../pages/compliance/TransactionAlerts'));
const AdminRegulatoryReports = lazy(() => import('../../pages/compliance/RegulatoryReports'));
const AdminCapitalAdequacy = lazy(() => import('../../pages/compliance/CapitalAdequacy'));
const AdminIFRS9Dashboard = lazy(() => import('../../pages/compliance/IFRS9Dashboard'));
const AdminRiskWeightedAssets = lazy(() => import('../../pages/compliance/RiskWeightedAssets'));
const KYBEntityList = lazy(() => import('../../pages/kyb/EntityList'));
const KYBEntityDetail = lazy(() => import('../../pages/kyb/EntityDetail'));
const KYBReviewQueue = lazy(() => import('../../pages/kyb/ReviewQueue'));
const KYBScreeningDashboard = lazy(() => import('../../pages/kyb/ScreeningDashboard'));
const KYBOwnershipStructure = lazy(() => import('../../features/kyb/pages/OwnershipStructure'));
const AdminTellerDashboard = lazy(() => import('../../pages/branch/TellerDashboard'));
const AdminVaultManagement = lazy(() => import('../../pages/branch/VaultManagement'));
const AdminEODReconciliation = lazy(() => import('../../pages/branch/EODReconciliation'));
const AdminBranchOverview = lazy(() => import('../../pages/branch/BranchOverview'));
const AdminNotificationTemplates = lazy(() => import('../../pages/notifications/NotificationTemplates'));
const AdminDeliveryHistory = lazy(() => import('../../pages/notifications/DeliveryHistory'));
const AdminChannelConfig = lazy(() => import('../../pages/notifications/ChannelConfig'));
const AdminCustomerPreferences = lazy(() => import('../../pages/notifications/CustomerPreferences'));
const AdminProductCatalog = lazy(() => import('../../pages/admin/products/ProductCatalog'));
const AdminProductDetail = lazy(() => import('../../pages/admin/products/ProductDetail'));
const AdminProductBuilder = lazy(() => import('../../pages/admin/products/ProductBuilder'));
const AdminProductComparison = lazy(() => import('../../pages/admin/products/ProductComparison'));
const AdminLimitDefinitions = lazy(() => import('../../pages/admin/limits/LimitDefinitions'));
const AdminInstitutionLimits = lazy(() => import('../../pages/admin/limits/InstitutionLimits'));
const AdminCustomerLimitOverrides = lazy(() => import('../../pages/admin/limits/CustomerLimitOverrides'));
const AdminLimitHistory = lazy(() => import('../../pages/admin/limits/LimitHistory'));
const AdminProviderCatalog = lazy(() => import('../../pages/admin/providers/ProviderCatalog'));
const AdminProviderContracts = lazy(() => import('../../pages/admin/providers/ProviderContracts'));
const AdminProviderHealth = lazy(() => import('../../pages/admin/providers/ProviderHealth'));
const AdminFeatureMatrix = lazy(() => import('../../pages/admin/providers/FeatureMatrix'));
const AdminProviderSwap = lazy(() => import('../../pages/admin/providers/ProviderSwap'));
const AdminApiMetering = lazy(() => import('../../pages/admin/providers/ApiMetering'));
const AdminFeeDefinitions = lazy(() => import('../../pages/admin/fees/FeeDefinitions'));
const AdminFeeSchedules = lazy(() => import('../../pages/admin/fees/FeeSchedules'));
const AdminFeeSimulator = lazy(() => import('../../pages/admin/fees/FeeSimulator'));
const AdminOpenBankingConfig = lazy(() => import('../../pages/admin/openbanking/OpenBankingConfig'));
const AdminConnectionMonitor = lazy(() => import('../../pages/admin/openbanking/ConnectionMonitor'));
const AdminOpenBankingAlerts = lazy(() => import('../../pages/admin/openbanking/OpenBankingAlerts'));
const AdminOpenBankingStats = lazy(() => import('../../pages/admin/openbanking/OpenBankingStats'));
const AdminConsentOversight = lazy(() => import('../../pages/admin/openbanking/ConsentOversight'));
const AdminConfigBrowser = lazy(() => import('../../pages/admin/config/ConfigBrowser'));
const AdminConfigHistory = lazy(() => import('../../pages/admin/config/ConfigHistory'));
const AdminConfigExportImport = lazy(() => import('../../pages/admin/config/ConfigExportImport'));
const AdminRulesConfig = lazy(() => import('../../pages/admin/RulesConfig'));
const AdminPersonalityManager = lazy(() => import('../../pages/admin/neural/PersonalityManager'));
const AdminNeuralRules = lazy(() => import('../../pages/admin/neural/NeuralRules'));
const AdminFeedbackDashboard = lazy(() => import('../../pages/admin/neural/FeedbackDashboard'));
const AdminConversationBrowser = lazy(() => import('../../pages/admin/neural/ConversationBrowser'));
const AdminMoniAIFeedbackIssues = lazy(() => import('../../pages/admin/moni-ai/FeedbackIssues'));

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const JourneyBuilderRedirect = () => {
    const { id } = useParams<{ id: string }>();
    return <Navigate to={`/journey-templates/${id}`} replace />;
};

// ---------------------------------------------------------------------------
// administrationRoutes()
// ---------------------------------------------------------------------------

export function administrationRoutes() {
    return (
        <>
        {/* Compliance Routes */}
        <Route path={ROUTES.COMPLIANCE.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.COMPLIANCE)}>
                <ComplianceDashboard />
            </ProtectedRoute>
        } />
        <Route path={ROUTES.COMPLIANCE_ALERTS.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.COMPLIANCE_ALERTS)}>
                <AlertManagement />
            </ProtectedRoute>
        } />
        <Route path={ROUTES.COMPLIANCE_SAR.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.COMPLIANCE_SAR)}>
                <SarFiling />
            </ProtectedRoute>
        } />
        <Route path={ROUTES.COMPLIANCE_REGULATORY_RETURNS.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.COMPLIANCE_REGULATORY_RETURNS)}>
                <RegulatoryReturns />
            </ProtectedRoute>
        } />
        <Route path={ROUTES.COMPLIANCE_SANCTIONS.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.COMPLIANCE_SANCTIONS)}>
                <SanctionsScreening />
            </ProtectedRoute>
        } />

        {/* Compliance Centre – Phase 9C.9 */}
        <Route path={ROUTES.COMPLIANCE_SAR_MANAGEMENT.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.COMPLIANCE_SAR_MANAGEMENT)}>
                <AdminSARManagement />
            </ProtectedRoute>
        } />
        <Route path={ROUTES.COMPLIANCE_SANCTIONS_SCREENING.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.COMPLIANCE_SANCTIONS_SCREENING)}>
                <AdminSanctionsScreening />
            </ProtectedRoute>
        } />
        <Route path={ROUTES.COMPLIANCE_TRANSACTION_ALERTS.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.COMPLIANCE_TRANSACTION_ALERTS)}>
                <AdminTransactionAlerts />
            </ProtectedRoute>
        } />
        <Route path={ROUTES.COMPLIANCE_REGULATORY_REPORTS.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.COMPLIANCE_REGULATORY_REPORTS)}>
                <AdminRegulatoryReports />
            </ProtectedRoute>
        } />
        <Route path={ROUTES.COMPLIANCE_CAPITAL_ADEQUACY.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.COMPLIANCE_CAPITAL_ADEQUACY)}>
                <AdminCapitalAdequacy />
            </ProtectedRoute>
        } />
        <Route path={ROUTES.COMPLIANCE_IFRS9.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.COMPLIANCE_IFRS9)}>
                <AdminIFRS9Dashboard />
            </ProtectedRoute>
        } />
        <Route path={ROUTES.COMPLIANCE_RWA.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.COMPLIANCE_RWA)}>
                <AdminRiskWeightedAssets />
            </ProtectedRoute>
        } />

        {/* KYB Centre – Phase 9C.10 */}
        <Route path={ROUTES.KYB_ENTITIES.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.KYB_ENTITIES)}>
                <KYBEntityList />
            </ProtectedRoute>
        } />
        <Route path={ROUTES.KYB_ENTITIES_DETAIL.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.KYB_ENTITIES_DETAIL)}>
                <KYBEntityDetail />
            </ProtectedRoute>
        } />
        <Route path={ROUTES.KYB_REVIEWS.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.KYB_REVIEWS)}>
                <KYBReviewQueue />
            </ProtectedRoute>
        } />
        <Route path={ROUTES.KYB_SCREENING.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.KYB_SCREENING)}>
                <KYBScreeningDashboard />
            </ProtectedRoute>
        } />
        <Route path={ROUTES.KYB_OWNERSHIP_DETAIL.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.KYB_OWNERSHIP_DETAIL)}>
                <KYBOwnershipStructure />
            </ProtectedRoute>
        } />

        {/* Branch Banking Integration – Phase 9C.11 */}
        <Route path={ROUTES.BRANCH_TELLER.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.BRANCH_TELLER)}>
                <AdminTellerDashboard />
            </ProtectedRoute>
        } />
        <Route path={ROUTES.BRANCH_VAULT.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.BRANCH_VAULT)}>
                <AdminVaultManagement />
            </ProtectedRoute>
        } />
        <Route path={ROUTES.BRANCH_EOD.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.BRANCH_EOD)}>
                <AdminEODReconciliation />
            </ProtectedRoute>
        } />
        <Route path={ROUTES.BRANCH_OVERVIEW.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.BRANCH_OVERVIEW)}>
                <AdminBranchOverview />
            </ProtectedRoute>
        } />

        {/* Notifications Centre – Phase 9C.14 */}
        <Route path={ROUTES.NOTIFICATIONS_TEMPLATES.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.NOTIFICATIONS_TEMPLATES)}>
                <AdminNotificationTemplates />
            </ProtectedRoute>
        } />
        <Route path={ROUTES.NOTIFICATIONS_HISTORY.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.NOTIFICATIONS_HISTORY)}>
                <AdminDeliveryHistory />
            </ProtectedRoute>
        } />
        <Route path={ROUTES.NOTIFICATIONS_CHANNELS.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.NOTIFICATIONS_CHANNELS)}>
                <AdminChannelConfig />
            </ProtectedRoute>
        } />
        <Route path={ROUTES.NOTIFICATIONS_PREFERENCES.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.NOTIFICATIONS_PREFERENCES)}>
                <AdminCustomerPreferences />
            </ProtectedRoute>
        } />

        {/* Product Factory – Phase 9C.12 */}
        <Route path={ROUTES.PRODUCTS.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.PRODUCTS)}>
                <AdminProductCatalog />
            </ProtectedRoute>
        } />
        <Route path={ROUTES.PRODUCTS_COMPARE.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.PRODUCTS_COMPARE)}>
                <AdminProductComparison />
            </ProtectedRoute>
        } />
        <Route path={ROUTES.PRODUCTS_DETAIL.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.PRODUCTS_DETAIL)}>
                <AdminProductDetail />
            </ProtectedRoute>
        } />
        <Route path={ROUTES.PRODUCTS_BUILDER.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.PRODUCTS_BUILDER)}>
                <AdminProductBuilder />
            </ProtectedRoute>
        } />

        {/* Limits & Controls – Phase 9C.13 */}
        <Route path={ROUTES.LIMITS.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.LIMITS)}>
                <AdminLimitDefinitions />
            </ProtectedRoute>
        } />
        <Route path={ROUTES.LIMITS_INSTITUTION.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.LIMITS_INSTITUTION)}>
                <AdminInstitutionLimits />
            </ProtectedRoute>
        } />
        <Route path={ROUTES.LIMITS_CUSTOMER_OVERRIDES.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.LIMITS_CUSTOMER_OVERRIDES)}>
                <AdminCustomerLimitOverrides />
            </ProtectedRoute>
        } />
        <Route path={ROUTES.LIMITS_HISTORY.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.LIMITS_HISTORY)}>
                <AdminLimitHistory />
            </ProtectedRoute>
        } />

        {/* Insights Engine Admin – Phase 9CX.3 */}
        <Route path={ROUTES.ADMIN_INSIGHTS_DESIGNER.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.ADMIN_INSIGHTS_DESIGNER)}>
                <Navigate to="/insights" replace />
            </ProtectedRoute>
        } />
        <Route path={ROUTES.ADMIN_INSIGHTS_RULES.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.ADMIN_INSIGHTS_RULES)}>
                <Navigate to="/insights" replace />
            </ProtectedRoute>
        } />
        <Route path={ROUTES.ADMIN_INSIGHTS_SCHEDULES.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.ADMIN_INSIGHTS_SCHEDULES)}>
                <Navigate to="/insights?tab=schedules" replace />
            </ProtectedRoute>
        } />

        {/* Profile Engine Admin – Phase 9CX.4 */}
        <Route path={ROUTES.ADMIN_PROFILES_DESIGNER.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.ADMIN_PROFILES_DESIGNER)}>
                <Navigate to="/profile-traits" replace />
            </ProtectedRoute>
        } />
        <Route path={ROUTES.ADMIN_PROFILES_TRAITS.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.ADMIN_PROFILES_TRAITS)}>
                <Navigate to="/profile-traits" replace />
            </ProtectedRoute>
        } />
        <Route path={ROUTES.ADMIN_PROFILES_OVERRIDES.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.ADMIN_PROFILES_OVERRIDES)}>
                <Navigate to="/profile-traits?tab=overrides" replace />
            </ProtectedRoute>
        } />

        {/* Journey Engine Admin – Phase 9CX.5 */}
        <Route path={ROUTES.ADMIN_JOURNEYS_TEMPLATES.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.ADMIN_JOURNEYS_TEMPLATES)}>
                <Navigate to="/journey-templates" replace />
            </ProtectedRoute>
        } />
        <Route path={ROUTES.ADMIN_JOURNEYS_BUILDER_DETAIL.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.ADMIN_JOURNEYS_BUILDER_DETAIL)}>
                <JourneyBuilderRedirect />
            </ProtectedRoute>
        } />
        <Route path={ROUTES.ADMIN_JOURNEYS_VERSIONS_DETAIL.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.ADMIN_JOURNEYS_VERSIONS_DETAIL)}>
                <Navigate to="/journey-templates" replace />
            </ProtectedRoute>
        } />

        {/* Provider Registry Admin – Phase 9CX.6 */}
        <Route path={ROUTES.ADMIN_PROVIDERS.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.ADMIN_PROVIDERS)}>
                <AdminProviderCatalog />
            </ProtectedRoute>
        } />
        <Route path={ROUTES.ADMIN_PROVIDERS_CONTRACTS.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.ADMIN_PROVIDERS_CONTRACTS)}>
                <AdminProviderContracts />
            </ProtectedRoute>
        } />
        <Route path={ROUTES.ADMIN_PROVIDERS_HEALTH.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.ADMIN_PROVIDERS_HEALTH)}>
                <AdminProviderHealth />
            </ProtectedRoute>
        } />
        <Route path={ROUTES.ADMIN_PROVIDERS_FEATURES.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.ADMIN_PROVIDERS_FEATURES)}>
                <AdminFeatureMatrix />
            </ProtectedRoute>
        } />
        <Route path={ROUTES.ADMIN_PROVIDERS_SWAP.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.ADMIN_PROVIDERS_SWAP)}>
                <AdminProviderSwap />
            </ProtectedRoute>
        } />
        <Route path={ROUTES.ADMIN_PROVIDERS_METERING.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.ADMIN_PROVIDERS_METERING)}>
                <AdminApiMetering />
            </ProtectedRoute>
        } />

        {/* Fee Engine Admin – Phase 9CX.7 */}
        <Route path={ROUTES.ADMIN_FEES.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.ADMIN_FEES)}>
                <AdminFeeDefinitions />
            </ProtectedRoute>
        } />
        <Route path={ROUTES.ADMIN_FEES_SCHEDULES.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.ADMIN_FEES_SCHEDULES)}>
                <AdminFeeSchedules />
            </ProtectedRoute>
        } />
        <Route path={ROUTES.ADMIN_FEES_SIMULATOR.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.ADMIN_FEES_SIMULATOR)}>
                <AdminFeeSimulator />
            </ProtectedRoute>
        } />

        {/* Rewards Engine Full Admin – Phase 9CX.8 */}
        <Route path={ROUTES.ADMIN_REWARDS.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.ADMIN_REWARDS)}>
                <Navigate to="/rewards/config" replace />
            </ProtectedRoute>
        } />
        <Route path={ROUTES.ADMIN_REWARDS_BADGES.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.ADMIN_REWARDS_BADGES)}>
                <Navigate to="/rewards/badges" replace />
            </ProtectedRoute>
        } />
        <Route path={ROUTES.ADMIN_REWARDS_MILESTONES.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.ADMIN_REWARDS_MILESTONES)}>
                <Navigate to="/rewards/badges" replace />
            </ProtectedRoute>
        } />
        <Route path={ROUTES.ADMIN_REWARDS_SCHEMES.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.ADMIN_REWARDS_SCHEMES)}>
                <Navigate to="/rewards/config" replace />
            </ProtectedRoute>
        } />
        <Route path={ROUTES.ADMIN_REWARDS_ANALYTICS.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.ADMIN_REWARDS_ANALYTICS)}>
                <Navigate to="/rewards/analytics" replace />
            </ProtectedRoute>
        } />

        {/* Open Banking Admin (Phase 9CX.9) */}
        <Route path={ROUTES.ADMIN_OPENBANKING_CONFIG.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.ADMIN_OPENBANKING_CONFIG)}>
                <AdminOpenBankingConfig />
            </ProtectedRoute>
        } />
        <Route path={ROUTES.ADMIN_OPENBANKING_CONNECTIONS.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.ADMIN_OPENBANKING_CONNECTIONS)}>
                <AdminConnectionMonitor />
            </ProtectedRoute>
        } />
        <Route path={ROUTES.ADMIN_OPENBANKING_ALERTS.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.ADMIN_OPENBANKING_ALERTS)}>
                <AdminOpenBankingAlerts />
            </ProtectedRoute>
        } />
        <Route path={ROUTES.ADMIN_OPENBANKING_STATS.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.ADMIN_OPENBANKING_STATS)}>
                <AdminOpenBankingStats />
            </ProtectedRoute>
        } />
        <Route path={ROUTES.ADMIN_OPENBANKING_CONSENTS.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.ADMIN_OPENBANKING_CONSENTS)}>
                <AdminConsentOversight />
            </ProtectedRoute>
        } />

        {/* ConfigStore Admin (Phase 9CX.12) */}
        <Route path={ROUTES.ADMIN_CONFIG.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.ADMIN_CONFIG)}>
                <AdminConfigBrowser />
            </ProtectedRoute>
        } />
        <Route path={ROUTES.ADMIN_CONFIG_HISTORY.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.ADMIN_CONFIG_HISTORY)}>
                <AdminConfigHistory />
            </ProtectedRoute>
        } />
        <Route path={ROUTES.ADMIN_CONFIG_EXPORT_IMPORT.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.ADMIN_CONFIG_EXPORT_IMPORT)}>
                <AdminConfigExportImport />
            </ProtectedRoute>
        } />

        {/* Rules Config – Phase 9CX.1 */}
        <Route path={ROUTES.ADMIN_RULES.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.ADMIN_RULES)}>
                <AdminRulesConfig />
            </ProtectedRoute>
        } />

        {/* Neural Engine Admin – Phase 9CX.2 */}
        <Route path={ROUTES.ADMIN_NEURAL_PERSONALITY.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.ADMIN_NEURAL_PERSONALITY)}>
                <AdminPersonalityManager />
            </ProtectedRoute>
        } />
        <Route path={ROUTES.ADMIN_NEURAL_RULES.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.ADMIN_NEURAL_RULES)}>
                <AdminNeuralRules />
            </ProtectedRoute>
        } />
        <Route path={ROUTES.ADMIN_NEURAL_FEEDBACK.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.ADMIN_NEURAL_FEEDBACK)}>
                <AdminFeedbackDashboard />
            </ProtectedRoute>
        } />
        <Route path={ROUTES.ADMIN_NEURAL_CONVERSATIONS.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.ADMIN_NEURAL_CONVERSATIONS)}>
                <AdminConversationBrowser />
            </ProtectedRoute>
        } />
        <Route path={ROUTES.ADMIN_MONI_AI_FEEDBACK.path} element={
            <ProtectedRoute requiredPermissions={permissionsFor(ROUTES.ADMIN_MONI_AI_FEEDBACK)}>
                <AdminMoniAIFeedbackIssues />
            </ProtectedRoute>
        } />
        </>
    );
}
